import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { getTravelInfo, MODE_EMOJI } from '../../services/travelTimeService';
import { getBriefingMode } from '../../hooks/useMorningBriefing';

// ── Markdown stripper — removes common markdown syntax from AI-generated text ──
function stripMarkdown(text) {
  if (!text) return text;
  return text
    .replace(/^#{1,6}\s+/gm, '')          // # headings
    .replace(/\*\*(.+?)\*\*/g, '$1')       // **bold**
    .replace(/\*(.+?)\*/g, '$1')           // *italic*
    .replace(/^[-*+]\s+/gm, '')            // - bullet points
    .replace(/^\d+\.\s+/gm, '')            // 1. numbered lists
    .replace(/`{1,3}[^`]*`{1,3}/g, '$&'.replace(/`/g, '')) // `code`
    .replace(/_{1,2}(.+?)_{1,2}/g, '$1')   // __bold__ / _italic_
    .replace(/~~(.+?)~~/g, '$1')           // ~~strikethrough~~
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [link](url)
    .replace(/\n{3,}/g, '\n\n')            // collapse excess blank lines
    .trim();
}

// Split AI text into summary sentence (first line) and body (remainder)
function parseSummary(text) {
  if (!text) return { summaryLine: null, bodyText: null };
  const idx = text.indexOf('\n');
  if (idx === -1) return { summaryLine: text, bodyText: null };
  return {
    summaryLine: text.slice(0, idx).trim() || null,
    bodyText: text.slice(idx).trim() || null,
  };
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const NAVY = '#0C2D48';
const GREEN = '#27ae60';
const RED = '#E96A6A';
const BORDER = '#e0e0e0';

// ── Mode config ───────────────────────────────────────────────────────────────
const MODE_CONFIG = {
  morning:   { icon: '☀️',  title: 'Morning Briefing',   greeting: 'Good morning,',   loading: 'Generating your morning briefing…' },
  afternoon: { icon: '🌤',  title: 'Afternoon Check-in', greeting: 'Good afternoon,', loading: 'Preparing your afternoon check-in…' },
  evening:   { icon: '🌙',  title: 'Evening Wrap-up',    greeting: 'Good evening,',   loading: 'Preparing your evening wrap-up…' },
};

// Left border accent per time of day
const BORDER_COLORS = {
  morning:   '#f59e0b',
  afternoon: '#3b82f6',
  evening:   '#7c3aed',
};

// ── Card shell ────────────────────────────────────────────────────────────────
const cardStyle = {
  background: '#fff',
  border: '1px solid #35454C',
  borderRadius: 14,
  boxShadow: '0 2px 12px rgba(12,45,72,0.07)',
  marginBottom: 0,
  overflow: 'hidden',
  fontFamily: "'Raleway', sans-serif",
  color: '#242629',
  display: 'flex',
  flexDirection: 'column',
};

const rowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.5rem',
  padding: '0.35rem 0',
};

// ── Priority dot ──────────────────────────────────────────────────────────────
function PriorityDot({ priority }) {
  const colors = { Critical: '#dc2626', High: '#f59e0b', Medium: '#FBC490', Low: '#9ca3af' };
  return (
    <span style={{
      width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 5,
      background: colors[priority] || NAVY, display: 'inline-block',
    }} />
  );
}

// ── Section block ─────────────────────────────────────────────────────────────
function BriefingSection({ icon, title, children }) {
  return (
    <div>
      <p style={{
        margin: '0 0 0.6rem',
        fontSize: '0.72rem', fontWeight: 700, color: '#aaa',
        textTransform: 'uppercase', letterSpacing: '0.07em',
        fontFamily: "'Poppins', sans-serif",
      }}>
        {icon}&nbsp; {title}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        {children}
      </div>
    </div>
  );
}

// Email-detected events awaiting confirmation — shown in all three briefings.
// Informational only; the user confirms via the calendar banner or Aidey chat.
function DetectedEventsSection({ eventProposals = [] }) {
  if (!eventProposals.length) return null;
  const fmt = (p) => {
    const d = new Date(p.event_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    return `${p.title} — ${d}${p.event_time ? ` ${p.event_time}` : ''}`;
  };
  return (
    <BriefingSection icon="📨" title="Detected events">
      {eventProposals.slice(0, 5).map((p, i) => {
        const clash = p.clash_result || {};
        const clashes = (clash.checked !== false && Array.isArray(clash.clashes)) ? clash.clashes : [];
        return (
          <div key={p.id || i} style={{ fontSize: '0.88rem', color: '#555', lineHeight: 1.5 }}>
            {fmt(p)}
            {clashes.length > 0 && <span style={{ color: '#b91c1c', fontWeight: 600 }}> · clashes</span>}
          </div>
        );
      })}
      <div style={{ fontSize: '0.78rem', color: '#999', marginTop: 2 }}>
        Ask Aidey to go through them, or confirm from your calendar.
      </div>
    </BriefingSection>
  );
}

function EmptyRow({ children }) {
  return (
    <p style={{
      margin: 0, fontSize: '0.88rem', color: '#aaa',
      fontStyle: 'italic', fontFamily: "'Raleway', sans-serif",
    }}>
      {children}
    </p>
  );
}

// ── Shared meeting row ────────────────────────────────────────────────────────
function MeetingRow({ m, i }) {
  const { currentUser } = useCurrentUser();
  const [travelInfo, setTravelInfo] = useState(null);

  useEffect(() => {
    if (!m.location) return;
    const tp = currentUser?.preferences?.travel || {};
    const origin = tp.homeAddress || tp.workplaceAddress;
    if (!origin) return;
    let cancelled = false;
    getTravelInfo(origin, m.location)
      .then(result => { if (!cancelled) setTravelInfo(result); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [m.location, currentUser]);

  const travelPrefs = currentUser?.preferences?.travel || {};
  const mapsOrigin = travelPrefs.homeAddress || travelPrefs.workplaceAddress;
  const fallbackBuffer = travelPrefs.travelBuffer || 60;

  return (
    <div key={i} style={rowStyle}>
      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: NAVY, minWidth: 52, marginTop: 1 }}>
        {m.time}
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: NAVY }}>{m.title}</div>
        {m.location && (
          <div style={{ fontSize: '0.77rem', color: '#888', marginTop: 2 }}>
            {m.location}
            {mapsOrigin && (
              <span>
                {' · '}
                {travelInfo
                  ? `${MODE_EMOJI[travelInfo.recommendedMode] || '🚗'} ~${travelInfo.bufferMins} min`
                  : `~${fallbackBuffer} min est.`}
                {' · '}
                <a
                  href={`https://www.google.com/maps/dir/${encodeURIComponent(mapsOrigin)}/${encodeURIComponent(m.location)}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: NAVY, textDecoration: 'underline' }}
                >
                  Maps
                </a>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Mode-specific expanded sections ──────────────────────────────────────────
function MorningDetail({ briefing }) {
  const { meetings = [], priorityTasks = [], invoiceAlerts = [], teamFlags = [], eventProposals = [], weather } = briefing;
  return (
    <>
      <BriefingSection icon="📅" title="Today's schedule">
        {meetings.length === 0 ? (
          <EmptyRow>No meetings today</EmptyRow>
        ) : meetings.map((m, i) => <MeetingRow key={i} m={m} i={i} />)}
      </BriefingSection>

      <BriefingSection icon="✅" title="Priority tasks">
        {priorityTasks.length === 0 ? (
          <EmptyRow>You're clear on tasks today</EmptyRow>
        ) : priorityTasks.map((t, i) => (
          <div key={i} style={rowStyle}>
            <PriorityDot priority={t.priority} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 600, color: NAVY }}>{t.title}</span>
              <span style={{ marginLeft: 7, fontSize: '0.75rem', color: '#aaa' }}>{t.priority}</span>
              {t.daysOverdue > 0 && (
                <span style={{
                  marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: RED,
                  background: 'rgba(233,106,106,0.1)', padding: '0.1rem 0.45rem', borderRadius: 4,
                }}>
                  {t.daysOverdue}d overdue
                </span>
              )}
            </div>
          </div>
        ))}
      </BriefingSection>

      {invoiceAlerts.length > 0 && (
        <BriefingSection icon="🧾" title="Invoice alerts">
          {invoiceAlerts.map((inv, i) => (
            <div key={i} style={rowStyle}>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: '0.88rem', fontWeight: 600, color: NAVY }}>{inv.clientName}</span>
                <span style={{ marginLeft: 8, fontSize: '0.82rem', color: '#555' }}>
                  {inv.currency}{Number(inv.amount).toLocaleString()}
                </span>
                {inv.daysOverdue > 0 ? (
                  <span style={{
                    marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: RED,
                    background: 'rgba(233,106,106,0.1)', padding: '0.1rem 0.45rem', borderRadius: 4,
                  }}>
                    {inv.daysOverdue}d overdue
                  </span>
                ) : (
                  <span style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: '#f59e0b' }}>
                    due today
                  </span>
                )}
              </div>
            </div>
          ))}
        </BriefingSection>
      )}

      {teamFlags.length > 0 && (
        <BriefingSection icon="🚩" title="Team flags">
          {teamFlags.map((f, i) => (
            <div key={i} style={{ ...rowStyle, flexDirection: 'column', gap: 2, padding: '0.4rem 0' }}>
              <div style={{ fontSize: '0.88rem', color: NAVY }}>
                <strong>{f.memberName}</strong>
                {' '}hasn't responded to{' '}
                <strong>"{f.taskName}"</strong>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#aaa' }}>Assigned {f.assignedDate} · 48h+ ago</div>
            </div>
          ))}
        </BriefingSection>
      )}

      {weather && (
        <BriefingSection icon="🌤" title="Weather">
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#555', lineHeight: 1.55 }}>{weather}</p>
        </BriefingSection>
      )}

      <DetectedEventsSection eventProposals={eventProposals} />
    </>
  );
}

function AfternoonDetail({ briefing }) {
  const {
    completedToday = [],
    outstandingToday = [],
    remainingMeetings = [],
    overdueFollowUps = [],
    tomorrowMeetings = [],
    eventProposals = [],
  } = briefing;

  // Merge overdue tasks into "still to do" so overdue items always count as outstanding.
  // Deduplicate by title in case both arrays somehow contain the same task.
  const outstandingTitles = new Set(outstandingToday.map(t => t.title));
  const stillToDo = [
    ...outstandingToday,
    ...overdueFollowUps.filter(t => !outstandingTitles.has(t.title)),
  ];

  return (
    <>
      <BriefingSection icon="✅" title="Completed today">
        {completedToday.length === 0 ? (
          <EmptyRow>Nothing marked complete yet</EmptyRow>
        ) : completedToday.map((t, i) => (
          <div key={i} style={rowStyle}>
            <span style={{ color: GREEN, fontSize: '0.8rem', marginTop: 2, flexShrink: 0 }}>✓</span>
            <span style={{ fontSize: '0.88rem', color: '#555' }}>{t.title}</span>
          </div>
        ))}
      </BriefingSection>

      <BriefingSection icon="⏳" title="Still to do">
        {stillToDo.length === 0 ? (
          <EmptyRow>All clear — nothing outstanding</EmptyRow>
        ) : stillToDo.map((t, i) => (
          <div key={i} style={rowStyle}>
            <PriorityDot priority={t.priority} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 600, color: NAVY }}>{t.title}</span>
              {t.time && (
                <span style={{ marginLeft: 7, fontSize: '0.75rem', color: '#aaa' }}>{t.time}</span>
              )}
              {t.daysOverdue > 0 && (
                <span style={{
                  marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: RED,
                  background: 'rgba(233,106,106,0.1)', padding: '0.1rem 0.45rem', borderRadius: 4,
                }}>
                  {t.daysOverdue}d overdue
                </span>
              )}
            </div>
          </div>
        ))}
      </BriefingSection>

      {remainingMeetings.length > 0 && (
        <BriefingSection icon="📅" title="Still ahead today">
          {remainingMeetings.map((m, i) => <MeetingRow key={i} m={m} i={i} />)}
        </BriefingSection>
      )}

      <BriefingSection icon="📆" title="Tomorrow's calendar">
        {tomorrowMeetings.length === 0 ? (
          <EmptyRow>Nothing scheduled yet for tomorrow</EmptyRow>
        ) : tomorrowMeetings.map((m, i) => <MeetingRow key={i} m={m} i={i} />)}
      </BriefingSection>

      <DetectedEventsSection eventProposals={eventProposals} />
    </>
  );
}

function EveningDetail({ briefing }) {
  const {
    completedToday = [],
    rolledOver = [],
    firstTomorrowMeeting,
    tomorrowMeetings = [],
    invoiceAlerts = [],
    eventProposals = [],
  } = briefing;

  return (
    <>
      <BriefingSection icon="🏁" title="What you accomplished">
        {completedToday.length === 0 ? (
          <EmptyRow>No tasks marked complete today</EmptyRow>
        ) : completedToday.map((t, i) => (
          <div key={i} style={rowStyle}>
            <span style={{ color: GREEN, fontSize: '0.8rem', marginTop: 2, flexShrink: 0 }}>✓</span>
            <span style={{ fontSize: '0.88rem', color: '#555' }}>{t.title}</span>
          </div>
        ))}
      </BriefingSection>

      {rolledOver.length > 0 && (
        <BriefingSection icon="↩️" title="Rolled to tomorrow">
          {rolledOver.map((t, i) => (
            <div key={i} style={rowStyle}>
              <PriorityDot priority={t.priority} />
              <span style={{ fontSize: '0.88rem', color: NAVY }}>{t.title}</span>
            </div>
          ))}
        </BriefingSection>
      )}

      <BriefingSection icon="🌅" title="Tomorrow starts with">
        {!firstTomorrowMeeting ? (
          <EmptyRow>Nothing in the diary yet</EmptyRow>
        ) : (
          <MeetingRow m={firstTomorrowMeeting} i={0} />
        )}
        {tomorrowMeetings.length > 1 && (
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.77rem', color: '#aaa', fontStyle: 'italic' }}>
            +{tomorrowMeetings.length - 1} more meeting{tomorrowMeetings.length > 2 ? 's' : ''}
          </p>
        )}
      </BriefingSection>

      {invoiceAlerts.length > 0 && (
        <BriefingSection icon="🧾" title="Invoice alerts">
          {invoiceAlerts.map((inv, i) => (
            <div key={i} style={rowStyle}>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: '0.88rem', fontWeight: 600, color: NAVY }}>{inv.clientName}</span>
                <span style={{ marginLeft: 8, fontSize: '0.82rem', color: '#555' }}>
                  {inv.currency}{Number(inv.amount).toLocaleString()}
                </span>
                {inv.daysOverdue > 0 ? (
                  <span style={{
                    marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: RED,
                    background: 'rgba(233,106,106,0.1)', padding: '0.1rem 0.45rem', borderRadius: 4,
                  }}>
                    {inv.daysOverdue}d overdue
                  </span>
                ) : (
                  <span style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: '#f59e0b' }}>
                    due today
                  </span>
                )}
              </div>
            </div>
          ))}
        </BriefingSection>
      )}

      <DetectedEventsSection eventProposals={eventProposals} />
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function BriefingCard({
  briefing,
  loading,
  onResendEmail,
  emailConnected,
  mode: modeProp,
  briefingTime,
}) {
  const { t } = useTranslation('common');
  const mode        = modeProp || getBriefingMode();
  const accentColor = BORDER_COLORS[mode] || '#f59e0b';

  const modeConfigI18n = {
    morning:   { icon: '☀️',  title: t('briefing.morning_title'),   greeting: t('briefing.good_morning'),   loading: t('briefing.loading_morning') },
    afternoon: { icon: '🌤',  title: t('briefing.afternoon_title'), greeting: t('briefing.good_afternoon'), loading: t('briefing.loading_afternoon') },
    evening:   { icon: '🌙',  title: t('briefing.evening_title'),   greeting: t('briefing.good_evening'),   loading: t('briefing.loading_evening') },
  };
  const config = modeConfigI18n[mode] || modeConfigI18n.morning;

  const storageKey = `briefing-expanded-${mode}`;
  const [expanded, setExpanded] = useState(() => {
    try { return sessionStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });

  function toggleExpanded() {
    const next = !expanded;
    setExpanded(next);
    try { sessionStorage.setItem(storageKey, String(next)); } catch {}
  }

  if (!briefing) {
    let placeholderText = config.loading;
    if (!loading) {
      if (mode === 'morning' && briefingTime) {
        const [h, m] = briefingTime.split(':').map(Number);
        const now = new Date();
        const isPast = now.getHours() > h || (now.getHours() === h && now.getMinutes() >= m);
        placeholderText = isPast
          ? t('briefing.loading_morning')
          : t('briefing.ready_at', { time: briefingTime });
      } else {
        placeholderText = `Generating your ${config.title.toLowerCase()}…`;
      }
    }
    return (
      <div style={{ ...cardStyle, borderLeft: `4px solid ${accentColor}`, justifyContent: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1.25rem 1.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>{config.icon}</span>
          <span style={{ fontSize: '0.9rem', color: '#888' }}>{placeholderText}</span>
        </div>
      </div>
    );
  }

  const { greeting } = briefing;
  const rawSummary = stripMarkdown(briefing.aiSummary);
  const { summaryLine, bodyText } = parseSummary(rawSummary);

  return (
    <div style={{ ...cardStyle, borderLeft: `4px solid ${accentColor}` }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '1rem 1.25rem 0.25rem', gap: '0.55rem',
      }}>
        <span style={{ fontSize: '1.1rem' }}>{config.icon}</span>
        <div>
          <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.95rem', color: NAVY }}>
            {config.title}
          </div>
          <div style={{ fontSize: '0.77rem', color: '#aaa', marginTop: 1, fontFamily: "'Raleway', sans-serif" }}>
            {greeting?.dateStr}
          </div>
        </div>
      </div>

      {/* ── Greeting ── */}
      <div style={{ padding: '0.25rem 1.25rem 0' }}>
        <span style={{ fontSize: '0.92rem', fontWeight: 700, color: NAVY, fontFamily: "'Poppins', sans-serif" }}>
          {config.greeting} {greeting?.name}.
        </span>
      </div>

      {/* ── Summary sentence — always visible ── */}
      {summaryLine && (
        <div style={{ padding: '0.6rem 1.5rem 0' }}>
          <p style={{
            margin: 0, fontSize: '0.9rem', color: '#444',
            lineHeight: 1.65, fontStyle: 'italic', fontFamily: "'Raleway', sans-serif",
          }}>
            "{summaryLine}"
          </p>
        </div>
      )}

      {/* ── See more / See less toggle ── */}
      <div style={{ padding: '0.55rem 1.5rem 0.85rem' }}>
        <button
          onClick={toggleExpanded}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontSize: '0.82rem',
            fontWeight: 700,
            color: accentColor,
            fontFamily: "'Raleway', sans-serif",
            letterSpacing: '0.01em',
          }}
        >
          {expanded ? 'See less ↑' : 'See more ↓'}
        </button>
      </div>

      {/* ── Expandable content ── */}
      <div style={{
        overflow: 'hidden',
        maxHeight: expanded ? '3000px' : '0',
        opacity: expanded ? 1 : 0,
        transition: 'max-height 0.4s ease, opacity 0.3s ease',
      }}>

        {/* Full AI body text */}
        {bodyText && (
          <div style={{ padding: '0 1.5rem 0.75rem' }}>
            <p style={{
              margin: 0, fontSize: '0.88rem', color: '#555',
              lineHeight: 1.65, fontFamily: "'Raleway', sans-serif",
            }}>
              {bodyText}
            </p>
          </div>
        )}

        {/* Resend email */}
        {emailConnected && (
          <div style={{ padding: '0 1.25rem 0.75rem' }}>
            <button
              onClick={onResendEmail}
              style={{
                background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6,
                cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, color: '#777',
                fontFamily: "'Raleway', sans-serif", padding: '0.3rem 0.75rem',
              }}
            >
              Resend to email
            </button>
          </div>
        )}

        {/* Detail sections */}
        <div style={{
          borderTop: `1px solid ${BORDER}`,
          padding: '1.25rem',
          display: 'flex', flexDirection: 'column', gap: '1.25rem',
        }}>
          {mode === 'morning'   && <MorningDetail   briefing={briefing} />}
          {mode === 'afternoon' && <AfternoonDetail briefing={briefing} />}
          {mode === 'evening'   && <EveningDetail   briefing={briefing} />}
        </div>
      </div>
    </div>
  );
}
